import type { ReactNode } from "react"
import { Link } from "zudoku/router"
import { Button } from "zudoku/ui/Button"
import { Badge } from "zudoku/ui/Badge"
import { Card, CardContent } from "zudoku/ui/Card"
import { ArrowRight, FlaskConical, ShieldCheck, Route, AlertTriangle } from "zudoku/icons"

const howItWorks = [
    { phase: "Application open", access: "Test APIs", purpose: "Build and validate readiness" },
    { phase: "Evaluation", access: "Test APIs (continued)", purpose: "Support ongoing due diligence, if required" },
    { phase: "Selection notified", access: "Live APIs provisioned", purpose: "Begin supervised sandbox operation" },
]

const glance = [
    {
        anchor: "#test-api-access",
        badge: "Available Now",
        title: "Test API Access",
        description: "Open to every applicant from the moment applications open.",
    },
    {
        anchor: "#live-api-access",
        badge: "After Selection",
        title: "Live API Access",
        description: "Production-grade access, issued only to admitted participants.",
    },
    {
        anchor: "#get-started",
        badge: "4 Steps",
        title: "Get Started",
        description: "Register, integrate, test, and apply with evidence in hand.",
    },
]

const Callout = ({ children }: { children: ReactNode }) => (
    <div className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm leading-relaxed">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-500" />
        <div>{children}</div>
    </div>
)

export const DeveloperAccess = ({ className }: { className?: string }) => (
    <section {...{ className }}>
        <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Test Your Solution Before You Apply</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                    Every applicant to the CBN Regulatory Sandbox has access to our test APIs{" "}
                    <span className="font-semibold text-primary">from the moment applications open</span>.
                    Use this window to build, integrate, and validate your solution against
                    sandbox-equivalent endpoints; so if you're selected, your team walks in{" "}
                    <span className="font-semibold text-primary">ready to operate from day one</span>.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    This isn't a formality. Applicants who demonstrate{" "}
                    <span className="font-semibold text-primary">working, tested integrations</span>{" "}
                    during the application phase strengthen their readiness profile for evaluation.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 not-prose mb-16">
                {glance.map((item) => (
                    <Link key={item.anchor} to={item.anchor} className="block">
                        <Card className="h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer">
                            <CardContent className="p-4">
                                <Badge variant="secondary" className="mb-2">{item.badge}</Badge>
                                <p className="font-semibold">{item.title}</p>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                            </CardContent>
                        </Card>
                    </Link>
                ))}
            </div>

            <div id="test-api-access" className="rounded-2xl bg-card border border-border p-8 mb-8 scroll-mt-[var(--header-height)]">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <FlaskConical className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Test API Access</h3>
                <p className="text-sm font-semibold text-primary mb-5">Available Now</p>

                <p className="mb-4 leading-relaxed text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Who can access it:</span> Any organisation
                    preparing an application for the CBN regulatory sandbox.
                </p>
                <p className="mb-3 leading-relaxed text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">What it's for:</span> Technical readiness.
                    Use the test environment to:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-1 text-muted-foreground text-sm">
                    <li><span className="font-medium text-primary">Validate</span> your API integration against CBN sandbox specifications</li>
                    <li><span className="font-medium text-primary">Run</span> end-to-end test transactions in a non-live environment</li>
                    <li><span className="font-medium text-primary">Surface and resolve</span> technical issues before evaluation begins</li>
                    <li><span className="font-medium text-primary">Generate evidence</span> of technical readiness to strengthen your application</li>
                </ul>
                <div className="mb-8">
                    <Callout>
                        <span className="font-semibold">What it isn't:</span> Test API access does not
                        constitute conditional approval, pre-selection, or a guarantee of admission into the
                        CBN sandbox. It is a preparation tool available to all applicants during the
                        application window.
                    </Callout>
                </div>
                <Button size="lg" asChild>
                    <a href="https://sandbox.cbn.gov.ng" target="_blank" rel="noopener noreferrer" className="flex items-center w-fit">
                        Get Test API Credentials
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                </Button>
            </div>

            <div id="live-api-access" className="rounded-2xl bg-card border border-border p-8 mb-8 scroll-mt-[var(--header-height)]">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Live API Access</h3>
                <p className="text-sm font-semibold text-primary mb-5">For Selected CBN Sandbox Participants</p>

                <p className="mb-4 leading-relaxed text-sm text-muted-foreground">
                    Live, production-grade APIs are provisioned{" "}
                    <span className="font-semibold text-primary">exclusively to applicants formally admitted</span>{" "}
                    into the CBN sandbox following evaluation. Live access unlocks:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-1 text-muted-foreground text-sm">
                    <li><span className="font-medium text-primary">Production sandbox environment</span> under CBN supervisory oversight</li>
                    <li><span className="font-medium text-primary">Real transaction testing</span> within approved operating parameters</li>
                    <li><span className="font-medium text-primary">Direct technical support</span> from the CBN Sandbox team</li>
                    <li><span className="font-medium text-primary">Progression pathway</span> toward full licensing consideration</li>
                </ul>
                <Callout>
                    Live credentials are issued{" "}
                    <span className="font-semibold">only after formal notification of selection</span>. Access
                    to test APIs during the application phase does{" "}
                    <span className="font-semibold">not</span> automatically transfer to live API access.
                </Callout>
            </div>

            <div id="how-it-works" className="rounded-2xl bg-card border border-border p-8 scroll-mt-[var(--header-height)]">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Route className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-6">How It Works</h3>
                <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/50">
                                <th className="text-left font-semibold px-6 py-3">Phase</th>
                                <th className="text-left font-semibold px-6 py-3">Access</th>
                                <th className="text-left font-semibold px-6 py-3">Purpose</th>
                            </tr>
                        </thead>
                        <tbody>
                            {howItWorks.map((row, i) => (
                                <tr key={row.phase} className={i < howItWorks.length - 1 ? "border-b border-border" : ""}>
                                    <td className="px-6 py-4 font-semibold">{row.phase}</td>
                                    <td className="px-6 py-4 text-muted-foreground">{row.access}</td>
                                    <td className="px-6 py-4 text-muted-foreground">{row.purpose}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </section>
)